import React, {MutableRefObject, RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {getRectOfNodes, useReactFlow} from 'reactflow';
import type {Node, ReactFlowInstance} from 'reactflow';

import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import BaseControlButton from 'ui-library/reactflow/controls/BaseControlButton';
import {isFlowchartElementHiddenBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import {centerNodeInVisibleCanvas} from 'ui-library/reactflow/viewport/centerNodeInVisibleCanvas';

import {getProcedureRunDesignFlowchartAddReplicateNodeId} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE} from './ProcedureRunDesignFlowchartColumnNode';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

// Flex sibling panels leave the wrapper equal to the visible canvas.
const NULL_PANEL_REF: RefObject<HTMLDivElement> = {current: null};

export interface UseProcedureRunDesignFlowchartAutoScrollArgs {
  conditions: Array<ConditionGroup>;
  activeMethodId: string | null;
  isEditable: boolean;
  /** Live nodes merged by the layout hooks. */
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  /** Marks in-canvas adds until they are detected. */
  lastAddSourceRef?: MutableRefObject<'inCanvas' | null>;
}

export interface UseProcedureRunDesignFlowchartAutoScrollResult {
  additionalAutoScrollControls: JSX.Element;
}

/** Counts replicate rows to detect additions. */
function getReplicateRowCount(conditions: Array<ConditionGroup>): number {
  return conditions.reduce((total, condition) => total + condition.numReplicates, 0);
}

function isRowNode(node: ProcedureRunDesignFlowchartLayoutNode): boolean {
  // Column overlays span the full height, so exclude them from row calculations.
  return node.type !== PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE;
}

function getRowNodeIds(nodes: Array<ProcedureRunDesignFlowchartLayoutNode>): Array<string> {
  return nodes.filter((node) => isRowNode(node)).map((node) => node.id);
}

function isNodeLaidOut(node: ProcedureRunDesignFlowchartLayoutNode): boolean {
  return (node.width ?? 0) > 0 && (node.height ?? 0) > 0 && !isFlowchartElementHiddenBeforeLayout(node);
}

type AddSource = 'inCanvas' | 'nonCanvas';

interface UsePendingRowAdditionArgs {
  conditions: Array<ConditionGroup>;
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  lastAddSourceRef?: MutableRefObject<'inCanvas' | null>;
}

interface PendingRowAddition {
  rowCount: number;
  preAddRowNodeIdsRef: MutableRefObject<Set<string>>;
  isScrollPendingRef: MutableRefObject<boolean>;
  pendingAddSourceRef: MutableRefObject<AddSource>;
}

function usePendingRowAddition({
  conditions,
  nodes,
  lastAddSourceRef,
}: UsePendingRowAdditionArgs): PendingRowAddition {
  const rowCount = getReplicateRowCount(conditions);
  const previousRowCountRef = useRef(rowCount);
  const preAddRowNodeIdsRef = useRef<Set<string>>(new Set(getRowNodeIds(nodes)));
  const isScrollPendingRef = useRef(false);
  const pendingAddSourceRef = useRef<AddSource>('nonCanvas');

  useEffect(() => {
    if (rowCount > previousRowCountRef.current) {
      preAddRowNodeIdsRef.current = new Set(getRowNodeIds(nodes));
      pendingAddSourceRef.current = lastAddSourceRef?.current === 'inCanvas' ? 'inCanvas' : 'nonCanvas';
      if (lastAddSourceRef != null) {
        lastAddSourceRef.current = null;
      }
      isScrollPendingRef.current = true;
    }
    previousRowCountRef.current = rowCount;
  }, [rowCount, nodes, lastAddSourceRef]);

  return {rowCount, preAddRowNodeIdsRef, isScrollPendingRef, pendingAddSourceRef};
}

interface NodeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AutoScrollAxes {
  horizontal: boolean;
  vertical: boolean;
}

interface GetAxesToCenterArgs {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: RefObject<HTMLDivElement>;
  horizontalBounds: NodeBounds;
  verticalBounds: NodeBounds;
  allowHorizontal: boolean;
}

/** Returns allowed axes that require panning, or null when the canvas cannot be measured. */
function getAxesToCenter({
  reactFlowInstance,
  reactFlowWrapper,
  horizontalBounds,
  verticalBounds,
  allowHorizontal,
}: GetAxesToCenterArgs): AutoScrollAxes | null {
  const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
  if (wrapperRect == null || wrapperRect.width <= 0 || wrapperRect.height <= 0) {
    return null;
  }

  const {x, y, zoom} = reactFlowInstance.getViewport();
  const nodeLeft = wrapperRect.left + horizontalBounds.x * zoom + x;
  const nodeRight = nodeLeft + horizontalBounds.width * zoom;
  const nodeTop = wrapperRect.top + verticalBounds.y * zoom + y;
  const nodeBottom = nodeTop + verticalBounds.height * zoom;

  return {
    horizontal: allowHorizontal && (nodeLeft < wrapperRect.left || nodeRight > wrapperRect.right),
    vertical: nodeTop < wrapperRect.top || nodeBottom > wrapperRect.bottom,
  };
}

interface AutoScrollTarget {
  horizontalAnchor: NodeBounds;
  verticalTarget: NodeBounds;
}

interface GetAutoScrollTargetArgs {
  reactFlowNodes: Array<Node>;
  newRowNodeIds: ReadonlySet<string>;
  addReplicateNodeId: string | null;
}

/** Selects visibility anchors and the bounds to center after an add. */
function getAutoScrollTarget({
  reactFlowNodes,
  newRowNodeIds,
  addReplicateNodeId,
}: GetAutoScrollTargetArgs): AutoScrollTarget | null {
  let addReplicateNode: Node | null = null;
  let horizontalAnchor: NodeBounds | null = null;
  let bottomRowY = Number.NEGATIVE_INFINITY;
  let bottomRowNodes: Array<Node> = [];

  for (const node of reactFlowNodes) {
    if (node.id === addReplicateNodeId) {
      addReplicateNode = node;
    }
    if (!newRowNodeIds.has(node.id)) {
      continue;
    }

    const bounds = getRectOfNodes([node]);
    if (horizontalAnchor == null || bounds.x < horizontalAnchor.x) {
      horizontalAnchor = bounds;
    }
    if (bounds.y > bottomRowY) {
      bottomRowY = bounds.y;
      bottomRowNodes = [node];
    } else if (bounds.y === bottomRowY) {
      bottomRowNodes.push(node);
    }
  }

  if (horizontalAnchor == null || bottomRowNodes.length === 0) {
    return null;
  }

  return {
    horizontalAnchor,
    verticalTarget: getRectOfNodes(
      addReplicateNode == null ? bottomRowNodes : [...bottomRowNodes, addReplicateNode]
    ),
  };
}

/** Pans to reveal newly added rows after layout settles. */
export function useProcedureRunDesignFlowchartAutoScroll({
  conditions,
  activeMethodId,
  isEditable,
  nodes,
  reactFlowWrapper,
  lastAddSourceRef,
}: UseProcedureRunDesignFlowchartAutoScrollArgs): UseProcedureRunDesignFlowchartAutoScrollResult {
  const reactFlowInstance = useReactFlow();
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const toggleAutoScroll = useCallback(() => {
    setIsAutoScrollEnabled((isEnabled) => !isEnabled);
  }, []);
  const {rowCount, preAddRowNodeIdsRef, isScrollPendingRef, pendingAddSourceRef} = usePendingRowAddition({
    conditions,
    nodes,
    lastAddSourceRef,
  });

  useEffect(() => {
    if (!isAutoScrollEnabled) {
      isScrollPendingRef.current = false;
      return;
    }

    if (!isScrollPendingRef.current) {
      return;
    }

    const newRowNodes = nodes.filter((node) => isRowNode(node) && !preAddRowNodeIdsRef.current.has(node.id));
    if (newRowNodes.length === 0 || !newRowNodes.every((node) => isNodeLaidOut(node))) {
      return;
    }

    const addReplicateNodeId =
      isEditable && activeMethodId != null
        ? getProcedureRunDesignFlowchartAddReplicateNodeId(activeMethodId)
        : null;

    // Store nodes include positionAbsolute.
    const reactFlowNodes = reactFlowInstance.getNodes();
    const newRowNodeIds = new Set(
      newRowNodes.filter((node) => node.id !== addReplicateNodeId).map((node) => node.id)
    );
    const target = getAutoScrollTarget({reactFlowNodes, newRowNodeIds, addReplicateNodeId});
    if (target == null) {
      return;
    }

    const axes = getAxesToCenter({
      reactFlowInstance,
      reactFlowWrapper,
      horizontalBounds: target.horizontalAnchor,
      verticalBounds: target.verticalTarget,
      allowHorizontal: pendingAddSourceRef.current === 'nonCanvas',
    });
    if (axes == null) {
      return;
    }
    if (!axes.horizontal && !axes.vertical) {
      isScrollPendingRef.current = false;
      return;
    }

    // Clear before panning to avoid repeat pans.
    isScrollPendingRef.current = false;
    centerNodeInVisibleCanvas({
      reactFlowInstance,
      reactFlowWrapper,
      leftSidepanelRef: NULL_PANEL_REF,
      rightSidepanelRef: NULL_PANEL_REF,
      nodePosition: {x: target.horizontalAnchor.x, y: target.verticalTarget.y},
      nodeWidth: target.horizontalAnchor.width,
      nodeHeight: target.verticalTarget.height,
      axes,
    });
  }, [
    rowCount,
    nodes,
    activeMethodId,
    isEditable,
    reactFlowInstance,
    reactFlowWrapper,
    isAutoScrollEnabled,
    preAddRowNodeIdsRef,
    isScrollPendingRef,
    pendingAddSourceRef,
  ]);

  const additionalAutoScrollControls = (
    <BaseControlButton
      icon={isAutoScrollEnabled ? 'location-crosshairs-slash' : 'location-crosshairs'}
      tooltip={isAutoScrollEnabled ? 'Turn off auto scroll' : 'Turn on auto scroll'}
      onClick={toggleAutoScroll}
      dataTestAttrs={{component: 'AutoScrollButton', element: 'root'}}
    />
  );

  return {additionalAutoScrollControls};
}
